<template>
  <div class="pdfv" ref="wrap">
    <div class="pdfv-toolbar" v-if="!loading && !error">
      <div class="pdfv-tb-group">
        <v-btn icon small @click="zoom(-0.2)" :disabled="scale <= 0.6"><v-icon small>mdi-minus</v-icon></v-btn>
        <span class="pdfv-tb-scale">{{ Math.round(scale * 100) }}%</span>
        <v-btn icon small @click="zoom(0.2)" :disabled="scale >= 3"><v-icon small>mdi-plus</v-icon></v-btn>
        <v-btn icon small @click="fitWidth" :title="$t('common.fitToWidth')"><v-icon small>mdi-arrow-expand-horizontal</v-icon></v-btn>
      </div>
      <div class="pdfv-tb-group">
        <v-btn
          small
          text
          class="pdfv-tb-copy"
          :loading="copying"
          :disabled="hasText === false"
          :title="hasText === false ? 'Ten PDF to skan — brak warstwy tekstowej' : 'Skopiuj cały tekst dokumentu'"
          @click="copyAll"
        >
          <v-icon small left>{{ copied ? 'mdi-check' : 'mdi-content-copy' }}</v-icon>
          {{ copied ? 'Skopiowano' : 'Kopiuj tekst' }}
        </v-btn>
        <span class="pdfv-tb-pages">{{ numPages }} {{ numPages === 1 ? 'strona' : 'stron' }}</span>
      </div>
    </div>

    <div v-if="loading" class="pdfv-state">
      <v-progress-circular indeterminate color="primary" />
      <div>Renderowanie PDF…</div>
    </div>

    <div v-else-if="error" class="pdfv-state">
      <v-icon large color="error">mdi-alert-circle-outline</v-icon>
      <div>{{ error }}</div>
    </div>

    <div v-show="!loading && !error" class="pdfv-pages" ref="pages"></div>
  </div>
</template>

<script>
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf';

const PDFJS_VERSION = pdfjsLib.version || '3.11.174';
pdfjsLib.GlobalWorkerOptions.workerSrc =
  `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${PDFJS_VERSION}/pdf.worker.min.js`;

export default {
  name: 'PdfViewer',

  props: {
    src: { type: String, required: true },
  },

  data: () => ({
    loading: true,
    error: '',
    pdf: null,
    numPages: 0,
    scale: 1.3,
    renderedScale: null,
    hasText: null,
    copying: false,
    copied: false,
  }),

  watch: {
    src() { this.load(); },
    scale() { this.debouncedRerender(); },
  },

  created() {
    this._renderToken = 0;
  },

  mounted() {
    this.load();
    window.addEventListener('resize', this.onResize);
  },

  beforeDestroy() {
    this._renderToken++;
    clearTimeout(this._copiedT);
    window.removeEventListener('resize', this.onResize);
    if (this.pdf) {
      try { this.pdf.destroy(); } catch (e) { /* noop */ }
    }
  },

  methods: {
    async load() {
      this.loading = true;
      this.error = '';
      this.hasText = null;
      try {
        if (this.pdf) { try { this.pdf.destroy(); } catch (e) { /* noop */ } }
        const task = pdfjsLib.getDocument({ url: this.src, withCredentials: false });
        this.pdf = await task.promise;
        this.numPages = this.pdf.numPages;
        await this.$nextTick();
        await this.renderAll();
      } catch (e) {
        console.error('PDF load failed:', e);
        this.error = 'Nie udało się wczytać PDF.';
      } finally {
        this.loading = false;
      }
    },
    async renderAll() {
      const container = this.$refs.pages;
      if (!container || !this.pdf) return;
      const token = ++this._renderToken;
      container.innerHTML = '';
      const dpr = window.devicePixelRatio || 1;
      for (let i = 1; i <= this.numPages; i++) {
        const page = await this.pdf.getPage(i);
        if (token !== this._renderToken) return;
        const viewport = page.getViewport({ scale: this.scale });
        const wrap = document.createElement('div');
        wrap.className = 'pdfv-page';
        // pdf.js pozycjonuje warstwę tekstową względem --scale-factor
        wrap.style.setProperty('--scale-factor', String(this.scale));
        wrap.style.width = viewport.width + 'px';
        wrap.style.height = viewport.height + 'px';
        const canvas = document.createElement('canvas');
        canvas.width = viewport.width * dpr;
        canvas.height = viewport.height * dpr;
        canvas.style.width = viewport.width + 'px';
        canvas.style.height = viewport.height + 'px';
        const ctx = canvas.getContext('2d');
        ctx.scale(dpr, dpr);
        wrap.appendChild(canvas);
        const textLayer = document.createElement('div');
        textLayer.className = 'pdfv-text-layer';
        wrap.appendChild(textLayer);
        const label = document.createElement('div');
        label.className = 'pdfv-page-num';
        label.textContent = i + ' / ' + this.numPages;
        wrap.appendChild(label);
        container.appendChild(wrap);
        await page.render({ canvasContext: ctx, viewport }).promise;
        if (token !== this._renderToken) return;
        await this.buildTextLayer(page, viewport, textLayer);
        if (token !== this._renderToken) return;
      }
      this.renderedScale = this.scale;
    },
    async buildTextLayer(page, viewport, container) {
      try {
        const textContent = await page.getTextContent();
        if (this.hasText === null || this.hasText === false) {
          this.hasText = textContent.items.some(it => (it.str || '').trim().length > 0);
        }
        await pdfjsLib.renderTextLayer({
          textContentSource: textContent,
          container,
          viewport,
        }).promise;
      } catch (e) {
        // skan bez warstwy tekstowej albo anulowany render — podgląd graficzny zostaje
        console.warn('Text layer failed:', e);
      }
    },
    async copyAll() {
      if (!this.pdf) return;
      this.copying = true;
      try {
        const parts = [];
        for (let i = 1; i <= this.numPages; i++) {
          const page = await this.pdf.getPage(i);
          const tc = await page.getTextContent();
          let line = '';
          const lines = [];
          for (const item of tc.items) {
            if (typeof item.str !== 'string') continue;
            line += item.str;
            if (item.hasEOL) { lines.push(line); line = ''; }
          }
          if (line) lines.push(line);
          parts.push(lines.join('\n'));
        }
        const text = parts.join('\n\n').trim();
        if (!text) {
          this.hasText = false;
          return;
        }
        await this.writeClipboard(text);
        this.copied = true;
        clearTimeout(this._copiedT);
        this._copiedT = setTimeout(() => { this.copied = false; }, 2500);
      } catch (e) {
        console.error('Copy text failed:', e);
      } finally {
        this.copying = false;
      }
    },
    async writeClipboard(text) {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        return;
      }
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    },
    debouncedRerender() {
      clearTimeout(this._rerenderT);
      this._rerenderT = setTimeout(() => this.renderAll(), 120);
    },
    zoom(delta) {
      const next = Math.min(3, Math.max(0.6, +(this.scale + delta).toFixed(2)));
      this.scale = next;
    },
    fitWidth() {
      const wrap = this.$refs.wrap;
      if (!wrap || !this.pdf) return;
      this.pdf.getPage(1).then(page => {
        const vp = page.getViewport({ scale: 1 });
        const avail = wrap.clientWidth - 48;
        if (avail > 0) this.scale = +(avail / vp.width).toFixed(2);
      });
    },
    onResize() {
      clearTimeout(this._resizeT);
      this._resizeT = setTimeout(() => {
        if (!this.loading && this.pdf) this.fitWidth();
      }, 200);
    },
  },
};
</script>

<style scoped>
.pdfv {
  display: flex;
  flex-direction: column;
  background: #e6e8ec;
  min-height: 100%;
  height: 100%;
}
.pdfv-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  background: #2c2f36;
  color: #fff;
  gap: 8px;
  flex-shrink: 0;
}
.pdfv-tb-group { display: flex; align-items: center; gap: 4px; }
.pdfv-tb-group .v-btn { color: #fff !important; }
.pdfv-tb-scale { font-size: 12px; min-width: 46px; text-align: center; }
.pdfv-tb-pages { font-size: 12px; color: #c7cad1; }
.pdfv-tb-copy { color: #fff !important; font-size: 12px; letter-spacing: 0; text-transform: none; }
.pdfv-tb-copy.v-btn--disabled { color: #8b8f98 !important; }

.pdfv-pages {
  flex: 1 1 auto;
  overflow: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}
.pdfv-state {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 48px;
  color: #555;
  background: #fff;
}
</style>

<!-- Strony i warstwa tekstowa powstają dynamicznie (bez atrybutu scoped), więc reguły muszą być globalne — zawężone do .pdfv -->
<style>
.pdfv .pdfv-page {
  position: relative;
  background: #fff;
  box-shadow: 0 6px 20px rgba(0,0,0,.18), 0 2px 4px rgba(0,0,0,.08);
  border-radius: 2px;
}
.pdfv .pdfv-page canvas { display: block; border-radius: 2px; }
.pdfv .pdfv-page-num {
  position: absolute;
  bottom: 6px;
  right: 10px;
  font-size: 10px;
  color: #999;
  background: rgba(255,255,255,.85);
  padding: 1px 6px;
  border-radius: 10px;
  pointer-events: none;
  z-index: 2;
}

/* Warstwa tekstowa pdf.js — niewidoczna, ale zaznaczalna i kopiowalna */
.pdfv .pdfv-text-layer {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;
  opacity: 0.25;
  line-height: 1;
  text-align: initial;
  text-size-adjust: none;
  -webkit-text-size-adjust: none;
  forced-color-adjust: none;
  z-index: 1;
}
.pdfv .pdfv-text-layer span,
.pdfv .pdfv-text-layer br {
  color: transparent;
  position: absolute;
  white-space: pre;
  cursor: text;
  transform-origin: 0% 0%;
}
.pdfv .pdfv-text-layer span.markedContent {
  top: 0;
  height: 0;
}
.pdfv .pdfv-text-layer ::selection { background: #2b6cff; }
</style>
